import { useCallback, useState } from 'react';

import { apiFetch } from '@/lib/api';

const POLL_MS = 5000;

/**
 * Turns the uploaded prose script into the shot-level production bible.
 *
 * This is the step that actually unblocks producing: `POST /produce` requires
 * `project.script`, which only exists once this job has run AND passed validation.
 * Shared by the setup checklist and the Source Material section so both can start it
 * without duplicating the polling.
 *
 * The job is asynchronous and reports completion by flipping `bible_status`, so this
 * polls the project rather than awaiting the request.
 */
export function useBibleGeneration(projectId: string | undefined) {
  const [generating, setGenerating] = useState(false);

  const generate = useCallback(async () => {
    if (!projectId || generating) return;
    setGenerating(true);
    try {
      const resp = await apiFetch(`/api/projects/${projectId}/translate`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      if (!resp.ok) {
        setGenerating(false);
        return;
      }
      const poll = setInterval(async () => {
        try {
          const data = await apiFetch(`/api/projects/${projectId}`).then((r) => r.json());
          if (data.bible_status === 'ready' || data.bible_status === 'failed') {
            clearInterval(poll);
            setGenerating(false);
            // A full reload is blunt but correct here: a finished bible changes the
            // script, the cast roster and the readiness gate all at once.
            window.location.reload();
          }
        } catch {
          /* keep polling */
        }
      }, POLL_MS);
    } catch {
      setGenerating(false);
    }
  }, [projectId, generating]);

  return { generating, generate };
}
